import { useEffect, useState } from "react";
import { toast } from "sonner";
import { XCircle, Loader2, AlertTriangle, Clock } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

// Student cancel — uses the existing cut-off (/reschedule-info + /cancel-any).
// Late cancels are allowed; a credit is only returned inside the allowed window.
const fmtLeft = (ms) => {
  const m = Math.max(0, Math.floor(ms / 60000));
  const d = Math.floor(m / 1440), h = Math.floor((m % 1440) / 60), mm = m % 60;
  return d ? `${d}d ${h}h` : h ? `${h}h ${mm}m` : `${mm}m`;
};

export function CutoffCountdown({ info, testid = "cancel-countdown" }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 30000); return () => clearInterval(t); }, []);
  if (!info?.cutoff_at) return null;
  const left = new Date(info.cutoff_at).getTime() - now;
  if (left > 0) {
    return (
      <div className="text-xs text-[#22C55E] inline-flex items-center gap-1.5" data-testid={testid}>
        <Clock className="w-3.5 h-3.5" /> {fmtLeft(left)} left to cancel or reschedule
      </div>
    );
  }
  return (
    <div className="text-xs text-[#FF9F1C] inline-flex items-center gap-1.5" data-testid={testid}>
      <Clock className="w-3.5 h-3.5" /> {info.cutoff_hours}h cancellation cut-off has passed
    </div>
  );
}

export function CancelEnrolment({ bookingId, paidByCredit = false, onCancelled, testid = "student-session-cancel" }) {
  const [info, setInfo] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const { refresh } = useAuth();
  const [reason, setReason] = useState("");
  useEffect(() => {
    if (!bookingId) return;
    api.get(`/bookings/${bookingId}/reschedule-info`).then((r) => setInfo(r.data)).catch(() => setInfo(null));
  }, [bookingId]);
  if (!info || info.status === "cancelled" || info.hours_to_start <= 0) return null;
  const late = new Date(info.cutoff_at).getTime() <= Date.now();

  const cancel = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/bookings/${bookingId}/cancel-any`, { reason: late ? reason.trim() : "", coach_override: false });
      if (data.late_cancellation && data.credit_message) toast.warning(data.credit_message, { duration: 8000 });
      else if (data.credit_message) toast.success(data.credit_message, { duration: 6000 });
      else toast.success("Your enrolment has been cancelled");
      refresh?.();
      onCancelled?.();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Couldn't cancel"); }
    finally { setBusy(false); setConfirming(false); }
  };
  if (!confirming) {
    return (
      <div className="flex flex-wrap items-center gap-3">
        <CutoffCountdown info={info} testid={`${testid}-countdown`} />
        <button type="button" onClick={() => setConfirming(true)} data-testid={testid}
          className="min-h-[44px] px-4 py-2 text-xs uppercase tracking-wider border border-[#404040] hover:border-[#FF4500] hover:text-[#FF4500] active:scale-95 transition-colors inline-flex items-center gap-1.5">
          <XCircle className="w-3.5 h-3.5" /> Cancel enrolment
        </button>
      </div>
    );
  }
  return (
    <div className="space-y-2 max-w-lg" data-testid={`${testid}-confirm`}>
      {late ? (
        <div className="text-xs text-[#FF9F1C] inline-flex items-start gap-2" data-testid={`${testid}-late-warning`}>
          <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
          This is inside the {info.cutoff_hours}-hour cancellation cut-off.{paidByCredit ? " Your credit will not be returned automatically." : " No automatic refund will be given."} Your coach will be told and can re-credit you if there is a valid reason.
        </div>
      ) : paidByCredit && (
        <div className="text-xs text-[#22C55E]" data-testid={`${testid}-refund-note`}>Your credit will be returned to your account.</div>
      )}
      {late && (
        <label className="block">
          <span className="label-mono text-[#A3A3A3]">Reason for your coach (optional)</span>
          <textarea value={reason} onChange={(e) => setReason(e.target.value)} maxLength={400} rows={2}
            placeholder="e.g. injured, work emergency, agreed with coach"
            data-testid={`${testid}-reason`}
            className="mt-1 w-full bg-[#141414] border border-[#262626] focus:border-[#CCFF00] focus:outline-none p-2 text-sm" />
        </label>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm">Cancel your place on this session?</span>
        <button type="button" onClick={cancel} disabled={busy} data-testid={`${testid}-yes`}
          className="px-3 py-1.5 text-xs uppercase tracking-wider font-bold bg-[#FF4500] text-white inline-flex items-center gap-1 disabled:opacity-50">
          {busy && <Loader2 className="w-3 h-3 animate-spin" />} Yes, cancel
        </button>
        <button type="button" onClick={() => setConfirming(false)} className="px-3 py-1.5 text-xs uppercase tracking-wider text-[#A3A3A3] hover:text-white" data-testid={`${testid}-no`}>Keep my place</button>
      </div>
    </div>
  );
}
